import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { Screen } from '@/components/layout'
import { Button, InlineError, TextField } from '@/components/primitives'
import { LoadingRegion, Skeleton } from '@/components/states'
import { ApiFailure, api, type AuthVerifyResponse } from '@/lib/api'
import { offeredMethods, type WebAuthMethod } from '@/lib/authMethods'
import {
  rememberSignInDestination,
  requestedSignInDestination,
  takeSignInDestination,
} from '@/lib/authDestination'
import { t, tServer } from '@/lib/copy'
import { pathFor, useNavigate, useRoute } from '@/lib/router'
import { useSession } from '@/lib/session'
import { completeMockGoogleSignIn, isMockInstalled, markMockAuthenticated } from '@/lib/mock'

type SignInState =
  | 'loading'
  | 'ready'
  | 'redirecting'
  | 'submitting'
  | 'completing'
  | 'cancelled'
  | 'error'

function GoogleButton({
  busy,
  disabled,
  completing = false,
  onClick,
}: {
  busy: boolean
  disabled: boolean
  completing?: boolean
  onClick: () => void
}) {
  return (
    <Button
      className="provider-button provider-button-google"
      block
      busy={busy}
      showBusyLabel
      disabled={disabled}
      onClick={onClick}
    >
      <img className="provider-icon" src="/google-g.svg" alt="" aria-hidden="true" />
      {completing
        ? t('signIn.completing')
        : busy
          ? t('signIn.redirecting.google')
          : t('signIn.provider.google')}
    </Button>
  )
}

/** Google-first Web auth with a collapsed, Server-advertised password fallback. */
export function SignInScreen() {
  const navigate = useNavigate()
  const route = useRoute()
  const { signedIn } = useSession()
  const [methods, setMethods] = useState<WebAuthMethod[] | null>(null)
  const [passwordOpen, setPasswordOpen] = useState(false)
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [state, setState] = useState<SignInState>(route.query.has('result') ? 'completing' : 'loading')
  const [error, setError] = useState<string | null>(null)

  const finishSignIn = useCallback((destination: 'dashboard' | 'upload') => {
    signedIn()
    if (destination === 'upload') navigate('upload', {}, { replace: true })
    else navigate('dashboard', {}, { replace: true })
  }, [navigate, signedIn])

  const loadMethods = useCallback(async (signal?: AbortSignal) => {
    setState('loading')
    setError(null)
    try {
      const next = offeredMethods(await api.auth.methods(signal))
      if (next.length === 0) throw new Error('No supported Web sign-in methods were advertised')
      setMethods(next)
      setState('ready')
    } catch (e) {
      if ((e as Error)?.name === 'AbortError') return
      setMethods([])
      setError(t('signIn.error.unavailable'))
      setState('error')
    }
  }, [])

  useEffect(() => {
    const result = route.query.get('result')
    if (!result) {
      const controller = new AbortController()
      void loadMethods(controller.signal)
      return () => controller.abort()
    }

    if (result === 'success') {
      finishSignIn(takeSignInDestination())
      return
    }
    if (result === 'betaAccessRequired') {
      navigate('betaAccess', {}, { replace: true })
      return
    }
    if (result === 'collision') {
      navigate('authCollision', {}, { replace: true, query: { result: 'collision' } })
      return
    }

    history.replaceState(null, '', pathFor('signIn'))
    const controller = new AbortController()
    void loadMethods(controller.signal).then(() => {
      if (result === 'cancelled') {
        setState('cancelled')
        return
      }
      setError(result === 'expired' ? t('signIn.error.expiredAttempt') : t('signIn.error.verification'))
      setState('error')
    })
    return () => controller.abort()
  }, [finishSignIn, loadMethods, navigate, route.query])

  const google = methods?.find((method) => method.id === 'google')
  const breakGlass = methods?.find((method) => method.id === 'password')
  const controlsDisabled = state === 'redirecting' || state === 'submitting' || state === 'completing'

  const startGoogle = () => {
    if (!google) return
    setState('redirecting')
    setError(null)
    const destination = requestedSignInDestination(route.query)
    rememberSignInDestination(destination)
    if (isMockInstalled()) {
      if (completeMockGoogleSignIn() === 'betaAccessRequired') {
        navigate('betaAccess', {}, { replace: true })
      } else {
        finishSignIn(destination)
      }
      return
    }
    window.location.assign(api.auth.googleWebStartUrl('signIn', '/sign-in'))
  }

  const completePassword = (result: AuthVerifyResponse) => {
    markMockAuthenticated(result.user.email)
    finishSignIn(requestedSignInDestination(route.query))
  }

  const submitPassword = (event: FormEvent) => {
    event.preventDefault()
    setState('submitting')
    setError(null)
    void api.auth.password(username, password).then(completePassword).catch((e: unknown) => {
      const failure = e instanceof ApiFailure ? e : null
      setError(
        failure?.status === 0
          ? t('signIn.error.network')
          : failure?.body.code === 'notFound' || failure?.body.code === 'providerUnavailable'
            ? t('signIn.error.configuration')
            : failure
              ? tServer(failure.copyKey, undefined, 'signIn.error.credentials')
              : t('signIn.error.network'),
      )
      setState('error')
    })
  }

  return (
    <Screen id="signIn" state={state} chrome="standalone">
      <div className="auth-panel stack">
        <div className="auth-heading">
          <h1 className="page-title">{t('signIn.title')}</h1>
          {google ? <p className="body-secondary">{t('signIn.subtitle.web')}</p> : null}
        </div>

        {state === 'loading' ? (
          <LoadingRegion>
            <Skeleton height="var(--auth-provider-height)" />
            <Skeleton width="40%" />
          </LoadingRegion>
        ) : state === 'completing' ? (
          <GoogleButton busy disabled completing onClick={() => undefined} />
        ) : methods?.length === 0 ? (
          <div className="stack">
            <InlineError>{error ?? t('signIn.error.unavailable')}</InlineError>
            <Button variant="secondary" block onClick={() => void loadMethods()}>
              {t('cta.retry')}
            </Button>
          </div>
        ) : (
          <div className="stack">
            {google ? (
              <GoogleButton busy={state === 'redirecting'} disabled={controlsDisabled} onClick={startGoogle} />
            ) : null}

            {state === 'cancelled' ? <p className="auth-notice" role="status">{t('signIn.cancelled')}</p> : null}

            {breakGlass ? (
              <>
                {google ? (
                  <div className="auth-divider" aria-hidden="true">
                    <span />
                    <span>{t('signIn.divider')}</span>
                    <span />
                  </div>
                ) : null}
                <Button
                  variant="quiet"
                  block
                  aria-expanded={passwordOpen}
                  disabled={controlsDisabled}
                  onClick={() => {
                    setPasswordOpen((open) => !open)
                    setError(null)
                    setState('ready')
                  }}
                >
                  {passwordOpen ? t('signIn.hidePassword') : t('signIn.usePassword')}
                </Button>
              </>
            ) : null}

            {breakGlass && passwordOpen ? (
              <form className="stack auth-break-glass" onSubmit={submitPassword}>
                <p className="caption">{t('signIn.password.helper')}</p>
                <TextField
                  label={t('signIn.username.label')}
                  autoComplete="username"
                  value={username}
                  onChange={(event) => setUsername(event.target.value)}
                />
                <TextField
                  label={t('signIn.password.label')}
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                />
                {error ? <InlineError>{error}</InlineError> : null}
                <Button
                  variant="primary"
                  block
                  type="submit"
                  busy={state === 'submitting'}
                  disabled={controlsDisabled || username.length === 0 || password.length === 0}
                >
                  {state === 'submitting' ? t('signIn.cta.passwordSubmitting') : t('signIn.cta.password')}
                </Button>
              </form>
            ) : error ? <InlineError>{error}</InlineError> : null}
          </div>
        )}
      </div>
    </Screen>
  )
}
